import { useEffect, useState } from "react";
import { api, API, formatError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Download, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

const REPORTS = ["leads", "clients", "cps", "visits", "bookings", "followups"];

export default function Reports() {
  const [report, setReport] = useState("leads");
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [views, setViews] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");

  const load = () => api.get(`/reports/${report}`).then(r => setData(r.data));
  const loadViews = () => api.get("/saved-views").then(r => setViews(r.data));

  useEffect(() => { load(); }, [report]);
  useEffect(() => { loadViews(); }, []);

  const download = async () => {
    const res = await fetch(`${API}/reports/${report}/export.csv`, { credentials: "include" });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${report}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded");
  };

  const saveView = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/saved-views", { name, report, filters: { q } });
      toast.success("View saved"); setSaveOpen(false); setName(""); loadViews();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  const applyView = (v) => { setReport(v.report); setQ(v.filters?.q || ""); };

  const filtered = data?.rows?.filter(r => {
    if (!q) return true;
    return Object.values(r).some(v => String(v || "").toLowerCase().includes(q.toLowerCase()));
  }) || [];

  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Intelligence</div>
          <h1 className="font-serif-display text-3xl">Reports & MIS</h1>
        </div>
        <div className="flex gap-2">
          <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="save-view-btn"><Save size={14} className="mr-1"/> Save View</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Save this report view</DialogTitle></DialogHeader>
              <div><Label>View Name</Label><Input value={name} onChange={e => setName(e.target.value)} data-testid="view-name-input"/></div>
              <DialogFooter><Button onClick={saveView} data-testid="view-save-submit">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Button onClick={download} data-testid="export-csv-btn"><Download size={14} className="mr-1"/> Export CSV</Button>
        </div>
      </div>

      {views.length > 0 && (
        <Card className="p-3 flex flex-wrap gap-2">
          <span className="text-[11px] uppercase tracking-widest text-muted-foreground self-center mr-2">Saved views</span>
          {views.map(v => (
            <button key={v.id} onClick={() => applyView(v)} className="text-xs px-3 py-1 rounded-full border border-border hover:bg-accent/10" data-testid={`saved-view-${v.name}`}>
              {v.name} <span className="text-muted-foreground">· {v.report}</span>
            </button>
          ))}
        </Card>
      )}

      <Tabs value={report} onValueChange={setReport}>
        <TabsList className="flex-wrap h-auto justify-start">
          {REPORTS.map(r => <TabsTrigger key={r} value={r} data-testid={`report-tab-${r}`}>{r}</TabsTrigger>)}
        </TabsList>
        <TabsContent value={report}>
          <Card className="p-4 mb-4">
            <Input placeholder="Filter rows across all columns…" value={q} onChange={e => setQ(e.target.value)} data-testid="report-filter-input"/>
            <div className="text-xs text-muted-foreground mt-2">Showing <span className="metric-value">{filtered.length}</span> of {data?.rows?.length || 0}</div>
          </Card>
          <Card className="overflow-auto max-h-[65vh]">
            <table className="w-full text-xs">
              <thead className="bg-secondary/50 sticky top-0 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>{(data?.columns || []).map(c => <th key={c} className="text-left px-3 py-2 whitespace-nowrap">{c.replace(/_/g," ")}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={i} className="border-t border-border hover:bg-accent/5">
                    {(data?.columns || []).map(c => <td key={c} className="px-3 py-2 whitespace-nowrap max-w-[240px] truncate">{String(r[c] ?? "")}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
